import { IconButton } from '@civilgrid/ui/components';
import type { ColumnDef } from '@tanstack/react-table';
import { deleteSingleFile as apiDeleteSingleFile, getCivilSearchFileDownloadUrl } from '~/api/civilsearch';
import { downloadAndTrackFile } from '~/common/trackFileDownload';
import type { CivilGridFile } from '~/common/types';
import { Notify } from '~/components2/Notify';
import { usePendingDownload } from '~/hooks/usePendingDownload';
import DeleteDialog from '~/pages/admin/civilsearch/common/DeleteDialog';
import store from '../../store';
import FileViewer from './FileViewer';

export const columns: ColumnDef<CivilGridFile>[] = [
  {
    id: 'name',
    header: 'File Name',
    accessorKey: 'name',
  },
  {
    id: 'uploadedBy',
    header: 'Uploaded By',
    accessorFn: (f) => f.uploadedBy.email,
  },
  {
    id: 'createdAt',
    header: 'Uploaded At',
    accessorKey: 'createdAt',
    minSize: 300,
    cell: ({ getValue }) => {
      const date = getValue<Date>();
      const d = new Date(date);
      const time = d.toLocaleTimeString([], {
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
      });
      const day = d.toLocaleDateString();

      return (
        <div>
          {time} {day}
        </div>
      );
    },
  },
  {
    id: 'actions',
    header: 'Actions',
    size: 150,
    enableSorting: false,
    cell: (cell) => {
      const file = cell.row.original;
      const { pendingId, track } = usePendingDownload();
      const id = file.id;

      const onDelete = async () => {
        try {
          await apiDeleteSingleFile(store.project!.id, id);

          Notify.info({ message: 'File successfully deleted' });
          cell.table.options.meta?.refreshData?.();
        } catch {
          Notify.error({ message: 'Error deleting file' });
        }
      };

      return (
        <div className="flex w-full items-center space-x-2">
          <DeleteDialog
            title="Delete File"
            trigger={
              <IconButton
                variant="hollow"
                icon="trash"
                aria-label={`Delete ${file.name}`}
                data-testid={`supporting-file-delete-${file.id}`}
              />
            }
            onConfirm={onDelete}
          />

          <FileViewer file={file} />

          <IconButton
            variant="hollow"
            icon="download"
            aria-label={`Download ${file.name}`}
            data-testid={`supporting-file-download-${file.id}`}
            loading={pendingId === file.id}
            onClick={() =>
              track(file.id, () =>
                downloadAndTrackFile(file, { source: 'supporting_file', project_id: store.project?.id }, () =>
                  getCivilSearchFileDownloadUrl('supporting-file', file.id, store.project?.id),
                ),
              )
            }
          />
        </div>
      );
    },
  },
];
